import { useEffect, useState } from "react";
import { toast } from "sonner";
import { DollarSign, Coins, Info, Send, Check, Clock, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { cashoutInfo, cashoutRequest, formatApiError } from "@/lib/api";
import { CASHOUT } from "@/constants/testIds";

export default function CashoutPanel() {
  const { user, refresh } = useAuth();
  const [info, setInfo] = useState(null);
  const [coins, setCoins] = useState(500);
  const [method, setMethod] = useState("paypal");
  const [handle, setHandle] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => cashoutInfo().then(setInfo);
  useEffect(() => { load(); }, []);

  if (!info) return null;
  const netCoins = Math.max(0, coins - info.fee_coins);
  const netUsd = (netCoins * info.rate_usd_per_coin).toFixed(2);

  const submit = async () => {
    if (coins < info.min_coins) { toast.error(`Minimum ${info.min_coins} coins`); return; }
    if (!handle.trim()) { toast.error("Enter your payout handle"); return; }
    setBusy(true);
    try {
      await cashoutRequest(Number(coins), method, handle.trim());
      toast.success(`Cashout submitted · $${netUsd}`, { description: "Pending admin approval" });
      setHandle("");
      await refresh();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid={CASHOUT.root} className="glass rounded-3xl p-6 md:p-8 border border-[#39FF14]/25 relative overflow-hidden">
      <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#39FF14] mb-2">// cash out</div>
      <h3 className="font-heading font-black text-3xl uppercase tracking-tighter flex items-center gap-2">
        <DollarSign className="w-6 h-6 text-[#39FF14]" /> Convert Coins to Cash
      </h3>
      <p className="text-white/60 text-sm mt-2 max-w-xl">
        Everyone can cash out. 100 coins = $1.00. Flat fee of {info.fee_coins} coins (${(info.fee_coins * info.rate_usd_per_coin).toFixed(2)}) per payout.
        Minimum {info.min_coins} coins to withdraw.
      </p>

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="space-y-4">
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Amount (coins)</div>
            <input data-testid={CASHOUT.amountInput} type="number" min={info.min_coins} max={user.coins} step="10"
              value={coins} onChange={(e) => setCoins(Number(e.target.value) || 0)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#39FF14]/60" />
            <div className="mono text-[10px] text-white/40 mt-1">Balance: {user.coins} coins</div>
          </label>
          <div className="flex gap-2">
            {[500, 1000, 2500, 5000].map((n) => (
              <button key={n} disabled={n > user.coins} onClick={() => setCoins(n)}
                className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-white/10 hover:border-[#39FF14]/50 text-white/70 disabled:opacity-30">
                {n}
              </button>
            ))}
            <button disabled={user.coins < info.min_coins} onClick={() => setCoins(user.coins)}
              className="mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-full border border-[#39FF14]/40 text-[#39FF14] hover:bg-[#39FF14]/10 disabled:opacity-30">
              Max
            </button>
          </div>
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">Method</div>
            <select data-testid={CASHOUT.methodSelect} value={method} onChange={(e) => setMethod(e.target.value)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none">
              <option value="paypal">PayPal</option>
              <option value="bank">Bank transfer</option>
              <option value="crypto">Crypto (USDC)</option>
            </select>
          </label>
          <label className="block">
            <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-1.5">
              {method === "paypal" ? "PayPal email" : method === "bank" ? "IBAN or account ref" : "Wallet address"}
            </div>
            <input data-testid={CASHOUT.handleInput} value={handle} onChange={(e) => setHandle(e.target.value)}
              placeholder={method === "paypal" ? "you@example.com" : "…"}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-[#39FF14]/60" />
          </label>
        </div>

        {/* Summary */}
        <div className="space-y-3">
          <SummaryRow label="Amount" value={`${coins} coins`} />
          <SummaryRow label="Fee" value={`- ${info.fee_coins} coins`} negative />
          <SummaryRow label="Net" value={`${netCoins} coins`} bold />
          <div className="border-t border-white/10 pt-3">
            <SummaryRow label="You receive" value={`$${netUsd}`} big />
          </div>
          <button data-testid={CASHOUT.submit} onClick={submit} disabled={busy || coins < info.min_coins}
            className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#39FF14] hover:bg-white text-black font-bold mono text-xs uppercase tracking-widest py-3 rounded-full disabled:opacity-40 transition-colors">
            <Send className="w-4 h-4" /> {busy ? "Submitting…" : `Request $${netUsd}`}
          </button>
          <p className="mono text-[10px] text-white/40 flex items-center gap-1">
            <Info className="w-3 h-3" /> Payouts reviewed within 24h · non-refundable once approved
          </p>
        </div>
      </div>

      {info.history?.length > 0 && (
        <div className="mt-8" data-testid={CASHOUT.history}>
          <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-3">// your payouts</div>
          <div className="space-y-2">
            {info.history.map((c) => (
              <div key={c.id} className="glass rounded-xl p-3 flex items-center gap-3">
                <StatusIcon status={c.status} />
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold text-sm">${c.usd} via {c.method}</div>
                  <div className="mono text-[10px] text-white/50">{new Date(c.created_at).toLocaleString()} · {c.net_coins} coins net</div>
                </div>
                <span className="mono text-[10px] uppercase text-white/50">{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, negative, bold, big }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="mono text-xs text-white/50 uppercase tracking-widest">{label}</span>
      <span className={`${big ? "font-heading font-black text-3xl" : bold ? "font-bold" : ""} ${negative ? "text-[#FF003C]" : big ? "text-[#39FF14]" : "text-white/90"}`}>
        {value}
      </span>
    </div>
  );
}

function StatusIcon({ status }) {
  if (status === "approved") return <div className="w-8 h-8 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/40 flex items-center justify-center"><Check className="w-4 h-4 text-[#39FF14]" /></div>;
  if (status === "rejected") return <div className="w-8 h-8 rounded-full bg-[#FF003C]/10 border border-[#FF003C]/40 flex items-center justify-center"><XCircle className="w-4 h-4 text-[#FF003C]" /></div>;
  return <div className="w-8 h-8 rounded-full bg-[#FFE300]/10 border border-[#FFE300]/40 flex items-center justify-center"><Clock className="w-4 h-4 text-[#FFE300]" /></div>;
}
